import URI from "urijs";
import * as T from "./@types";
import { sortByFormAndStrength } from "./helpers";
import { config } from "./config";
import cookies from "browser-cookies";
import { ApiResponseError } from "./helpers/errors";
import * as TG from "./@types/guards";
import {
  MedigapPlanPremiumRange,
  MedigapPlanType,
  MedigapPlanPolicy,
} from "./pages/Medigap/utils/plans";

async function handleApiError(
  response: Response,
  body?: string
): Promise<Error | ApiResponseError> {
  const requestId = response.headers.get("x-request-id");
  const anonymousSessionId = cookies.get("mct-anonymous-session-id");
  const beneSessionId = cookies.get("sid");
  const { status, statusText, url } = response;

  const customInfo = {
    requestId,
    anonymousSessionId,
    beneSessionId,
    apiResponseStatus: status,
    apiResponseStatusText: statusText,
    apiRequestUrl: url,
    apiRequestBody: body,
  };

  try {
    const bodyJson: T.ApiErrorResponse = await response.json();

    const {
      code: apiErrorCode,
      error: apiError,
      message: apiErrorMessage,
      details,
    } = bodyJson;

    const apiResponseError = new ApiResponseError();
    apiResponseError.info = {
      ...customInfo,
      apiError,
      apiErrorMessage,
      apiErrorCode,
      apiResponseDetails: details ? details[0].serving_data : null,
    };

    return Promise.resolve(apiResponseError);
  } catch (e) {
    const apiResponseParseError = new ApiResponseError("apiResponseParseError");
    apiResponseParseError.info = customInfo;

    return Promise.resolve(apiResponseParseError);
  }
}

interface ResWithHeaders {
  body: { [x: string]: unknown };
  headers: Headers;
}

/**
 * This function does the 'heavy lifting' of querying resources from the API. There is a base URI brought in from [[config]], which params are added to, and then `fetch` grabs the resource.
 * Next, we check `response.ok` and either return an error or the JSON.
 * @param path The path to the sought-after resource
 * @param params Zero or more query params, added via URI.js
 * @param headers Zero or more headers to add to the request
 */
export async function get(
  path: string,
  params: {},
  headers: {} = {}
): Promise<ResWithHeaders> {
  const url = URI(
    config.USE_LOCAL_PROXY ? path : `${config.MCTAPI_URI}${path}`
  );

  Object.entries(params).forEach(([key, value]) => {
    url.addSearch(key, value);
  });

  const interaction = window.newrelic?.interaction();
  interaction?.setName(url.toString()).save();

  const response = await fetch(url.toString(), {
    credentials: "same-origin",
    headers: {
      "Fe-Ver": config.FE_VERSION,
      ...headers,
    },
  });

  interaction?.end();

  if (!response.ok) {
    throw await handleApiError(response);
  }

  return { headers: response.headers, body: await response.json() };
}

export async function post(
  path: string,
  body: object,
  params?: {}
): Promise<ResWithHeaders> {
  const url = URI(
    config.USE_LOCAL_PROXY ? path : `${config.MCTAPI_URI}${path}`
  );

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.addSearch(key, value);
    });
  }

  const interaction = window.newrelic?.interaction();
  interaction?.setName(url.toString()).save();

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Fe-Ver": config.FE_VERSION,
    },
    body: JSON.stringify(body),
    credentials: "same-origin",
  });

  interaction?.end();

  if (!response.ok) {
    throw await handleApiError(response, JSON.stringify(body));
  }

  return { headers: response.headers, body: await response.json() };
}

export interface APIChatInitParams {
  Language: string;
  CurrentView: string;
}

export async function getAPIChatInit({
  Language,
  CurrentView,
}: APIChatInitParams): Promise<T.ChatInit | undefined> {
  const res = await get("/beneinfo/init-chat", {
    Language,
    CurrentView,
  });

  return TG.isChatInit(res.body) ? res.body : undefined;
}

export async function getBeneDrugs(): Promise<T.PrescriptionDrug[]> {
  const res = await get("/beneinfo/get-drugs", {});

  return TG.hasPrescriptions(res.body.drugs) ? res.body.drugs : [];
}

export async function sendKeepAlive(): Promise<ResWithHeaders> {
  return await get("/beneinfo/keep-alive", {});
}

export async function saveBeneDrugs(
  drugs: T.PrescriptionDrug[]
): Promise<ResWithHeaders> {
  return await post("/beneinfo/save-drugs", { drugs });
}

export async function getCounty(fips: string): Promise<T.County | undefined> {
  const {
    body: { county },
  } = await get("/geography/fips", { fips });
  return TG.isCounty(county) ? county : undefined;
}

export async function getCounties(zipcode: string): Promise<T.County[]> {
  const {
    body: { counties },
  } = await get("/geography/counties", { zipcode });
  return TG.hasCounties(counties) ? counties : [];
}

export async function getDrugsByLetter(
  letter: string,
  year?: string
): Promise<T.AutocompleteDrug[]> {
  const query = year ? { year } : {};
  const {
    body: { drugs },
  } = await get(`/drugs/list/${letter}`, query);

  return TG.hasAutocompleteDrugs(drugs) ? drugs : [];
}

export async function getOECBundle({
  activity_id,
  zipcode,
  fips,
  language_id,
  contract_year,
}: {
  activity_id: string;
  zipcode: string;
  fips: string;
  language_id: string;
  contract_year: string;
}): Promise<T.OECBundle | undefined> {
  const { body } = await post("/beneinfo/oec-package", {
    activity_id,
    zipcode,
    fips,
    language_id,
    contract_year,
  });

  return TG.isOECBundle(body) ? body : undefined;
}

export async function getPharmacies(
  params: T.PharmaciesRequest
): Promise<T.PharmaciesResponse> {
  const { body } = await get(`/geography/pharmacies/address`, params);

  return TG.isPharmaciesResponse(body) ? body : Promise.reject("Server Error");
}

/**
 * This function queries the backend for a specific [[Plan]]
 * @param url The unique url for the desired plan.
 * @param queryParams an object containing extra parameters for the request
 * @param headers an object containing extra headers for the request
 */
export async function getPlan(
  id: string,
  queryParams: {} = {},
  headers: {} = {}
): Promise<T.Plan | undefined> {
  const {
    body: { plan_card },
  } = await get(`/plan/${id}`, queryParams, headers);
  return TG.isPlan(plan_card) ? plan_card : undefined;
}

export interface GetPlansParams {
  fips: string;
  plan_type?: T.PlanType | T.PlanType[];
  sort_order?: string;
  page?: number;
  year?: string;
  zip?: string;
  snp_type?: T.PlanSNPType[];
  sanctioned_only?: boolean;
}

/**
 * This function exists to get a collection of [[Plan]]s from the backend.
 * @default year The plan year, to make sure that only 'valid' plans are returned.
 */
export async function getPlans(
  params: GetPlansParams,
  body: {} = {}
): Promise<T.PlanResults | undefined> {
  const defaultParams: Omit<GetPlansParams, "fips"> = {
    plan_type: T.PlanType.MAPD,
    page: 0,
    year: "2020",
  };
  params = { ...defaultParams, ...params };

  const { body: resBody } = await post("/plans/search", body, params);

  return TG.isPlanResults(resBody) ? resBody : undefined;
}

/**
 * getPlanCard returns a [[SearchResultPlan]] for a single plan
 * @param id The plan id
 * @param npis The NPIS of the beneficiary's pharmacies
 * @param lis The beneficiary's [[LowIncomeSubsidyStatus]]
 * @param headers Any additional headers to add to the request
 */
export async function getPlanCard(
  id: string,
  npis: string[],
  lis: T.LowIncomeSubsidyStatus,
  headers: {} = {}
): Promise<T.SearchResultPlan | undefined> {
  const {
    body: { plan },
  } = await get(
    `/plan/card/${id}`,
    {
      npis,
      lis: lis ?? T.LowIncomeSubsidyStatus.LIS_NO_HELP,
    },
    headers
  );
  return TG.isSearchResultPlan(plan) ? plan : undefined;
}

export async function getPlanInfoForNextYear(
  id: string
): Promise<T.FullPlanId | undefined> {
  const res = await get(`/plan/next/${id}`, {});
  if (res.body.plans && Array.isArray(res.body.plans)) {
    const plan = res.body.plans[0];
    return {
      contract_id: plan.contract_id,
      contract_year: plan.plan_year,
      segment_id: plan.segment_id,
      plan_id: plan.plan_id,
    };
  }
  return undefined;
}

export async function getAutocompleteDrugList(
  name: string,
  year?: string
): Promise<T.AutocompleteDrug[] | undefined> {
  const query = year ? { name, year } : { name };
  const {
    body: { drugs },
  } = await get(`/drugs/autocomplete`, query);

  return TG.hasAutocompleteDrugs(drugs) ? drugs : undefined;
}

export async function getDrugDosages(
  rxcui: string,
  year?: string
): Promise<T.DrugDosageInfo | undefined> {
  const query = year ? { year } : {};
  const res = await get(`/drugs/${rxcui}/forms`, query);
  const { dosages } = res.body;

  if (TG.hasDrugDosages(dosages)) {
    let commonDosage = null;
    if (
      dosages.length &&
      dosages[0].packages.length &&
      dosages[0].packages[0].volume
    ) {
      commonDosage = dosages[0];
    }
    const dosageInfo: T.DrugDosageInfo = {
      commonDosage,
      dosages: dosages.sort(sortByFormAndStrength),
    };

    return dosageInfo;
  }
  return undefined;
}

export async function getBeneInfo(
  code: string
): Promise<T.BeneficiaryAndApiHeaders> {
  const res = await get(`/beneinfo/slsx/login`, { request_token: code });

  const beneficiary = TG.isBeneficiary(res.body) ? res.body : undefined;

  return { headers: res.headers, beneficiary };
}

export async function getCSRBeneInfo(
  token: string
): Promise<T.BeneficiaryAndApiHeaders> {
  const res = await get(`/beneinfo/login-csr`, { token });
  const beneficiary = TG.isBeneficiary(res.body) ? res.body : undefined;

  return { headers: res.headers, beneficiary };
}

export async function getDrugCosts(
  npis: string[],
  prescriptions: {
    ndc: string;
    quantity: string;
    frequency: T.PrescriptionFrequency;
  }[],
  planIds: T.FullPlanId[],
  lis = ""
): Promise<T.DrugCostsResponse | undefined> {
  const res = await post(`/drugs/cost`, {
    npis,
    prescriptions,
    lis,
    plans: planIds,
  });

  return TG.isDrugCostsResponse(res.body) ? res.body : undefined;
}

export async function getOtherDrugInfo(
  ndcs: string[],
  planUrl: string
): Promise<T.DrugInfo[] | undefined> {
  const {
    body: { drugInfoList },
  } = await post(`/drugs/info/${planUrl}`, { ndcs });

  return TG.hasDrugInfo(drugInfoList) ? drugInfoList : undefined;
}

export async function sendLogoutRequest(): Promise<{}> {
  return await get("/beneinfo/logout", {});
}

export async function getBeneOecStatus(
  year: string
): Promise<T.OecStatus | undefined> {
  const {
    body: { statuses },
  } = await get("/beneinfo/oec-status", { year });

  return TG.hasOecStatus(statuses) ? statuses[0] : undefined;
}

export async function submitEnrollment(
  enrollData: T.EnrollDataForAPI | T.EnrollData2021ForAPI
): Promise<T.EnrollResponse | undefined> {
  const { body } = await post(`/beneinfo/oec-enroll`, enrollData);

  return TG.isEnrollResponse(body) ? body : undefined;
}

export async function validateMbi(mbi = ""): Promise<{ valid: boolean }> {
  const {
    body: { valid },
  } = await post("/beneinfo/validate-mbi", { mbi });

  return TG.isBoolean(valid) ? { valid } : { valid: false };
}

export async function getMedigapPlanPremiumRanges(
  state: string,
  zipcode: string,
  demographics?: T.DemographicInfo
): Promise<MedigapPlanPremiumRange[]> {
  const {
    body: { plans },
  } = await get(`/medigap/plans`, {
    state,
    zipcode,
    ...demographics,
  });

  return TG.hasMedigapPlanPremiumRanges(plans) ? plans : [];
}

export async function getCwPremiumRanges(params: {
  year: string;
  zipcode: string;
  fips: string;
}): Promise<T.CwPremiumRanges | undefined> {
  const { body } = await get("/plans/premiumranges", {
    ...params,
    zip: params.zipcode,
  });

  return TG.isCwPremiumRangesResponse(body) ? body : undefined;
}

export async function getMedigapPlanPolicies(
  state: string,
  zipcode: string,
  medigapPlanType: MedigapPlanType,
  demographics?: T.DemographicInfo
): Promise<MedigapPlanPolicy[]> {
  const {
    body: { policies },
  } = await get(`/medigap/policies`, {
    medigap_plan_type: medigapPlanType,
    state,
    zipcode,
    ...demographics,
  });

  return TG.hasMedigapPlanPolicies(policies) ? policies : [];
}

export async function getPlanMetadata(): Promise<T.PlanDataVersion[]> {
  const {
    body: { versions },
  } = await get("/plan/data", {});

  return TG.hasPlanDataVersions(versions) ? versions : [];
}

export async function getOriginalMedicareStarRatings(
  fips: string,
  year: string
): Promise<T.StarRating[]> {
  const {
    body: { star_ratings },
  } = await get("/plans/omstarratings", { fips, year });

  return TG.hasStarRatings(star_ratings) ? star_ratings : [];
}
